import React from 'react';
import { Phone, Mail, MapPin, Clock4, Instagram, Facebook, MessageCircle } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Footer = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const year = new Date().getFullYear();

  const handleNav = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative">
      {/* Main Footer */}
      <div className="bg-slate-950 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-8 md:gap-10">
          <div className="md:col-span-4">
            <div className="inline-block bg-white p-2.5 rounded-2xl mb-4 shadow-lg shadow-black/20">
              <img src="/logo.png" alt="Royal Dental Assistant Academy" className="h-10 md:h-12 w-auto object-contain" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs mb-6">{t.footer.tagline}</p>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/royaldaacademy/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:bg-pink-500/10 hover:text-pink-500 hover:border-pink-500/30 transition-all">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/share/1AcAVf8mbm/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:bg-blue-600/10 hover:text-blue-500 hover:border-blue-600/30 transition-all">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.tiktok.com/@royaldaacademy" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:bg-slate-800 hover:text-white hover:border-slate-700 transition-all">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.01.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.12-3.44-3.17-3.64-5.41-.05-.56-.04-1.11.02-1.66.19-1.57.85-3.04 1.86-4.21 1.34-1.51 3.3-2.4 5.31-2.47.01 1.34.01 2.68 0 4.02-.91-.01-1.8.27-2.5.83-1.07.82-1.66 2.21-1.43 3.55.15.86.61 1.62 1.25 2.16.8.69 1.86 1.02 2.9 1.01 1.6-.01 2.96-.92 3.56-2.39.29-.68.42-1.43.43-2.18.04-4.89.02-9.78.02-14.67z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-[11px] uppercase tracking-[0.18em] text-amber-400 font-bold mb-4">{t.footer.contact}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t.footer.office}</div>
                  <a href="tel:7047410908" className="text-white hover:text-amber-300 transition-colors font-semibold">704-741-0908</a>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MessageCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">WhatsApp</div>
                  <a href="https://wa.me/19804217678" target="_blank" rel="noopener noreferrer" className="text-white hover:text-green-400 transition-colors font-semibold">980-421-7678</a>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t.footer.email}</div>
                  <a href="mailto:info@royaldaacademy.com" className="text-white hover:text-amber-300 transition-colors font-semibold break-all">info@royaldaacademy.com</a>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <Clock4 className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t.footer.hours}</div>
                  <div className="text-white font-semibold">
                    {t.footer.hoursValue.split('\n').map((line, index) => (
                      <span key={index} className={`block ${index === 1 ? 'italic' : ''}`}>
                        {line}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.18em] text-amber-400 font-bold mb-4">{t.footer.campuses}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <span className="text-slate-300">{t.footer.gastonia}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-blue-300 mt-0.5 shrink-0" />
                <span className="text-slate-300">{t.footer.lakeNorman}</span>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-[11px] uppercase tracking-[0.18em] text-amber-400 font-bold mb-4">{t.footer.quickLinks}</h4>
            <ul className="space-y-2 text-sm">
              <li><button onClick={() => handleNav('/program')} className="text-slate-300 hover:text-white transition-colors">{t.footer.programs}</button></li>
              <li><button onClick={() => handleNav('/schedule')} className="text-slate-300 hover:text-white transition-colors">{t.footer.schedule}</button></li>
              <li><button onClick={() => handleNav('/application')} className="text-slate-300 hover:text-white transition-colors">{t.footer.apply}</button></li>
              <li><button onClick={() => { navigate('/'); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="text-slate-300 hover:text-white transition-colors">{t.footer.contact}</button></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center md:items-baseline gap-4 text-sm text-slate-500">
          <div className="flex flex-wrap items-baseline justify-center md:justify-start gap-x-2 gap-y-1 text-center md:text-left">
            <span>&copy; {year} Royal Dental Assistant Academy. {t.footer.rights}</span>
            <span className="hidden md:inline text-slate-700">|</span>
            <span>
              {t.footer.discreetAccreditation}
              <a onClick={() => { navigate('/about'); setTimeout(() => document.getElementById('accreditation')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="ml-1 text-slate-400 hover:text-brand-primary transition-colors cursor-pointer underline underline-offset-2">
                {t.footer.viewAccreditation}
              </a>
            </span>
          </div>
          <a onClick={() => handleNav('/privacy')} className="text-slate-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            {lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
          </a>
        </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
